import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Patient } from '../../../model/Patient'
import { useInsiIdentityCheck } from '../insi'
import { MailPatient } from '../../../model/Mail'
import { useUserEnabledFeature } from '../../utils/user'
import { FeaturesType, isDefined } from '@follow/cdk'
import { useCreatePatient } from '../patients'
import { getNewPatientFromMail } from '../../../misc/insi.utilities'
import { useActionDispatch } from '../../utils'
import { addValid } from '../../../store/message'
import { mailKeys } from './mail.keys'
import { useSetAtom } from 'jotai'
import { partialMailPatientModalAtom } from '../../../state/mail'
import { patientsKeys } from '../patients/patients.keys'
import { useCreateTask } from '../task'
import { TaskCategory } from '../../../model/Task'
import { RESET } from 'jotai/utils'
import { InsiPatient } from '../../../model/Insi'
import { createResourceHistory } from '../../../data/resourceHistory/createResourceHistory'
import InsUtilities from '../../../features/ins/utilities/Ins.utilities'
import { InsEventTypeEnums } from '../../../features/ins/enums/insEventType.enums'
import { InsCheckTypeEnums } from '../../../features/ins/enums/insCheckType.enums'
import { ResourceHistoryMutationPayload } from '../../../data/resourceHistory/mapper'
import { InsStatusCheckTypeEnums } from '../../../features/ins/enums/insStatusCheckType.enums'
import { idVigilanceKeys } from '../identityVigilance/identityVigilance.keys'

interface UseCreatePatientFromMailVariables {
  patient: MailPatient
  mailId: string
  sender: string
  recipient: string
  bypassCheck?: boolean
  insiInError?: boolean
  fromTrustedSource?: boolean
}

export const useCreatePatientFromMail = () => {
  const queryClient = useQueryClient()
  const dispatchValid = useActionDispatch(addValid)
  const setPartialPatientModalState = useSetAtom(partialMailPatientModalAtom)

  const insiService = useUserEnabledFeature(FeaturesType.insiValidation)
  const mssAutoInsiFetch = useUserEnabledFeature(FeaturesType.mssAutoInsiFetch)

  const { mutateAsync: identityCheck } = useInsiIdentityCheck()
  const { mutateAsync: createPatient } = useCreatePatient()
  const { mutate: createTask } = useCreateTask()

  const mutation = useMutation<Patient | null, Error, UseCreatePatientFromMailVariables>({
    mutationFn: async ({ patient, bypassCheck, insiInError, fromTrustedSource }) => {
      let codeCR: string | null = null;
      // Bypass de la vérification si ces FF sont paramétrés
      if (!isDefined(insiService) || !mssAutoInsiFetch) {
        const unverifiedPatient = getNewPatientFromMail(
          patient,
          false,
          undefined,
          fromTrustedSource,
        )
        const createdPatient = await createPatient({ patient: unverifiedPatient })
        return createdPatient
      }

      let removeCriticalInformationFromPatient: boolean
      let insiCheck: InsiPatient | null = null

      // Vérification de l'identité
      try {
        const identityCheckResponse = await identityCheck({ patient })
        insiCheck = identityCheckResponse?.patient || null
        codeCR = identityCheckResponse?.codeCR || null;
        // Annulation du processus (par ex, si l'utilisateur ferme la modale insiConsult)
        if (insiCheck === null) return null
        // La validation a réussi
        removeCriticalInformationFromPatient = false
      } catch (e) {
        // La validation a échoué
        removeCriticalInformationFromPatient = true

        // Via une modale, on propose à l'utilisateur de créer le patient (avec un statut d'identité "temporaire")
        const inputPromise = new Promise<boolean>((resolve) => {
          setPartialPatientModalState({
            onSubmit: (createPatient) => resolve(createPatient),
          })
        })
        const shouldCreatePatient = await inputPromise

        if (!shouldCreatePatient) {
          return null
        }
      }
      const newPatient = await createPatient({
        patient: getNewPatientFromMail(
          patient,
          removeCriticalInformationFromPatient,
          insiCheck ?? undefined,
          fromTrustedSource,
        ),
        params: {
          external: true,
          forceInsiCheck: insiService === 'insiValidationRest',
        },
      })

      if (newPatient?.id) {
        const insLog = InsUtilities.generateNewInsPatientResourceHistory({
          eventType: InsEventTypeEnums.INS_CHECK,
          checkType: InsCheckTypeEnums.PATIENT_IDENTITY,
          status:  codeCR || InsStatusCheckTypeEnums.KO,
          patientId: `${newPatient?.id}`,
          extraData: {},
        })
        createResourceHistory(insLog as unknown as ResourceHistoryMutationPayload)

      }
      return newPatient
    },
    onSettled: () => {
      setPartialPatientModalState(RESET)
    },
    onSuccess(data, { mailId, sender, recipient }) {
      if (data === null) return

      dispatchValid('Patient créé avec succès')
      queryClient.invalidateQueries({ queryKey: mailKeys.detail(mailId) })
      queryClient.invalidateQueries({ queryKey: patientsKeys.lists })
      queryClient.invalidateQueries({ queryKey: idVigilanceKeys.lists })
      if (data.inseeNumber) {
        createResourceHistory({
          resourceId: data.id.toString(),
          resourceType: 'Patient',
          eventType: 'mss_insi_flux',
          extraData: {
            birthFirstName: data.birthFirstName,
            birthLastName: data.birthLastName,
            inseeNumber: data.inseeNumber,
            sender: sender,
            recipient: recipient,
          },
        })
      }

      if (
        insiService === 'insiValidationInApp' &&
        data.identityStatus.insiStatus.checked === 'FAILED'
      ) {
        createTask({
          title: "Échec de la validation de l'identité via l'INSi",
          autoCreated: true,
          category: TaskCategory.INFORMATION,
          patientId: data.id,
          isNotify: true,
        })
      }
    },
  })

  return mutation
}
